// SPDX-License-Identifier: MIT

// Built-in tracker loaders for common third-party pixels/tools. Each factory
// takes the site's ID and returns { onAccept, onReject, onRouteChange } to wire
// onto a consent category via the `trackers` option. The plugin knows HOW to
// load each tracker; the site supplies WHICH id — so the plugin stays generic.
//
// onAccept loads + records the entry page view (idempotent), onReject tears the
// tracker down (drops its globals/script/cookies so no further events fire),
// onRouteChange fires a page view on SPA navigation. All run in the browser when
// the category's consent is granted.
//
// Teardown is best-effort and never reloads the page. For pixels like Facebook
// this stops all explicit and future page-view events, but any automatic-event
// listeners the pixel script attached on load persist until the visitor's next
// full page load — the trade-off for not reloading on withdrawal.

const { injectGtm, GTM_FLAG } = require('./gtm');

/* eslint-disable */

// Deletes window globals (falling back to undefined if delete is disallowed).
const dropGlobals = (keys) => {
  keys.forEach((k) => {
    try {
      delete window[k];
    } catch (e) {
      window[k] = undefined;
    }
  });
};

// Removes <script> tags whose src contains `match`.
const removeScripts = (match) => {
  if (!document.querySelectorAll) return;
  document.querySelectorAll('script[src*="' + match + '"]').forEach((s) => {
    if (s.parentNode) s.parentNode.removeChild(s);
  });
};

// Expires the named cookies on the host and each parent domain, so an identifier
// set on a parent domain (e.g. _fbp on .example.com) is actually removed.
const clearCookies = (names) => {
  const host = (window.location && window.location.hostname) || '';
  const parts = host.split('.');
  const domains = [null]; // host-only, no domain attribute
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push('.' + parts.slice(i).join('.'));
  }
  names.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie =
        name + '=; path=/; max-age=0' + (domain ? '; domain=' + domain : '');
    });
  });
};

// ---------------------------------------------------------------------------
// Ad pixels
// ---------------------------------------------------------------------------
// Every ad pixel shares the same consent lifecycle, so they're built from one
// definition:
//   globals     window keys meaning "already loaded" ([0]) + all to drop on reject
//   scriptMatch src substring of the injected script, for teardown
//   cookies     cookies to expire on reject
//   install(id) injects the vendor snippet + init (only called when not loaded)
//   pageView()  fires a page view (optional; some tags record views on load)
//
// Fallback mode (spec.fallback === true): load the pixel only if it isn't already
// loaded (e.g. by GTM) after a grace period (spec.fallbackDelayMs, default 3000).
// This keeps a pixel that's also in GTM working if it's ever dropped there,
// without double-firing while both exist — and SPA page views fire only when the
// plugin installed the pixel (GTM fires its own). Otherwise the pixel loads
// immediately. resolveTrackers sets `fallback` automatically: on when a GTM
// container is declared in the config, off when it isn't (see resolveTrackers.js).
const definePixel = (def) => (id, spec = {}) => {
  const isFallback = spec.fallback === true;
  let ours = false;
  const loaded = () => !!window[def.globals[0]];
  const doInstall = () => {
    if (loaded()) return false; // already loaded elsewhere (e.g. GTM)
    def.install(id);
    ours = true;
    return true;
  };
  const page = () => {
    if (def.pageView) def.pageView();
  };
  return {
    onAccept() {
      if (isFallback) {
        setTimeout(() => {
          if (doInstall()) page();
        }, spec.fallbackDelayMs || 3000);
        return;
      }
      doInstall();
      page();
    },
    onReject() {
      // Tear down without reloading: drop globals, remove the script, clear cookies.
      if (!loaded()) return;
      dropGlobals(def.globals);
      if (def.scriptMatch) removeScripts(def.scriptMatch);
      if (def.cookies) clearCookies(def.cookies);
      ours = false;
    },
    onRouteChange() {
      // Skip if not loaded, or if it's a fallback pixel GTM (not us) loaded.
      if (!loaded() || (isFallback && !ours)) return;
      page();
    },
  };
};

// Facebook / Meta Pixel.
const facebookPixel = definePixel({
  globals: ['fbq', '_fbq'],
  scriptMatch: 'connect.facebook.net',
  cookies: ['_fbp', '_fbc'],
  install(id) {
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', id);
  },
  pageView() { window.fbq('track', 'PageView'); },
});

// Reddit Pixel.
const redditPixel = definePixel({
  globals: ['rdt'],
  scriptMatch: 'redditstatic.com/ads/pixel.js',
  cookies: ['_rdt_uuid'],
  install(id) {
    !(function (w, d) {
      var p = (w.rdt = function () {
        p.sendEvent ? p.sendEvent.apply(p, arguments) : p.callQueue.push(arguments);
      });
      p.callQueue = [];
      var t = d.createElement('script');
      t.src = 'https://www.redditstatic.com/ads/pixel.js'; t.async = !0;
      var s = d.getElementsByTagName('script')[0];
      s.parentNode.insertBefore(t, s);
    })(window, document);
    window.rdt('init', id);
  },
  pageView() { window.rdt('track', 'PageVisit'); },
});

// Pinterest Tag.
const pinterestPixel = definePixel({
  globals: ['pintrk'],
  scriptMatch: 's.pinimg.com/ct/core.js',
  cookies: ['_pinterest_ct_ua', '_pinterest_sess', '_pin_unauth', '_derived_epik'],
  install(id) {
    !(function (e) {
      window.pintrk = function () {
        window.pintrk.queue.push(Array.prototype.slice.call(arguments));
      };
      var n = window.pintrk; n.queue = []; n.version = '3.0';
      var t = document.createElement('script'); t.async = !0; t.src = e;
      var r = document.getElementsByTagName('script')[0];
      r.parentNode.insertBefore(t, r);
    })('https://s.pinimg.com/ct/core.js');
    window.pintrk('load', id);
  },
  pageView() { window.pintrk('page'); },
});

// LinkedIn Insight Tag. Records page views on load; no standard SPA hook.
const linkedInInsight = definePixel({
  globals: ['lintrk', '_linkedin_data_partner_ids'],
  scriptMatch: 'snap.licdn.com',
  cookies: ['li_sugr', 'bcookie', 'lidc', 'UserMatchHistory', 'AnalyticsSyncHistory', 'lms_ads', 'lms_analytics'],
  install(id) {
    window._linkedin_partner_id = String(id);
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id);
    (function (l) {
      if (!l) {
        window.lintrk = function (a, b) { window.lintrk.q.push([a, b]); };
        window.lintrk.q = [];
      }
      var s = document.getElementsByTagName('script')[0];
      var b = document.createElement('script');
      b.type = 'text/javascript'; b.async = true;
      b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
      s.parentNode.insertBefore(b, s);
    })(window.lintrk);
  },
});

// Microsoft Advertising / Bing UET.
const microsoftUet = definePixel({
  globals: ['uetq'],
  scriptMatch: 'bat.bing.com/bat.js',
  cookies: ['MUID', '_uetsid', '_uetvid', 'MR', 'MRAID', 'ANON'],
  install(id) {
    (function (w, d, t, r, u) {
      var f, n, i;
      w[u] = w[u] || [];
      f = function () {
        var o = { ti: id, enableAutoSpaTracking: true };
        o.q = w[u]; w[u] = new w.UET(o); w[u].push('pageLoad');
      };
      n = d.createElement(t); n.src = r; n.async = 1;
      n.onload = n.onreadystatechange = function () {
        var s = this.readyState;
        (s && s !== 'loaded' && s !== 'complete') || (f(), (n.onload = n.onreadystatechange = null));
      };
      i = d.getElementsByTagName(t)[0]; i.parentNode.insertBefore(n, i);
    })(window, document, 'script', '//bat.bing.com/bat.js', 'uetq');
  },
  pageView() { if (window.uetq && window.uetq.push) window.uetq.push('event', 'page_view'); },
});

// TikTok Pixel.
const tiktokPixel = definePixel({
  globals: ['ttq', 'TiktokAnalyticsObject'],
  scriptMatch: 'analytics.tiktok.com',
  cookies: ['_ttp'],
  install(id) {
    (function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = (w[t] = w[t] || []);
      ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
      ttq.setAndDefer = function (a, b) {
        a[b] = function () { a.push([b].concat(Array.prototype.slice.call(arguments, 0))); };
      };
      for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.load = function (e) {
        var u = 'https://analytics.tiktok.com/i18n/pixel/events.js';
        ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = u;
        var o = d.createElement('script'); o.type = 'text/javascript'; o.async = !0;
        o.src = u + '?sdkid=' + e + '&lib=' + t;
        var a = d.getElementsByTagName('script')[0]; a.parentNode.insertBefore(o, a);
      };
      ttq.load(id);
    })(window, document, 'ttq');
  },
  pageView() { if (window.ttq && window.ttq.page) window.ttq.page(); },
});

// X / Twitter Pixel.
const twitterPixel = definePixel({
  globals: ['twq'],
  scriptMatch: 'static.ads-twitter.com',
  cookies: ['personalization_id', 'muc_ads'],
  install(id) {
    !(function (e, t, n, s, u, a) {
      e.twq || ((s = e.twq = function () {
        s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
      }), (s.version = '1.1'), (s.queue = []), (u = t.createElement(n)), (u.async = !0),
      (u.src = 'https://static.ads-twitter.com/uwt.js'),
      (a = t.getElementsByTagName(n)[0]), a.parentNode.insertBefore(u, a));
    })(window, document, 'script');
    window.twq('config', id);
  },
  pageView() { if (window.twq) window.twq('track', 'PageView'); },
});

// Snapchat Pixel.
const snapchatPixel = definePixel({
  globals: ['snaptr'],
  scriptMatch: 'sc-static.net/scevent.min.js',
  cookies: ['_scid', 'sc_at', '_schn', 'X-AB'],
  install(id) {
    (function (e, t, n) {
      var a = (e.snaptr = function () {
        a.handleRequest ? a.handleRequest.apply(a, arguments) : a.queue.push(arguments);
      });
      a.queue = [];
      var s = 'script'; var r = t.createElement(s); r.async = !0; r.src = n;
      var u = t.getElementsByTagName(s)[0]; u.parentNode.insertBefore(r, u);
    })(window, document, 'https://sc-static.net/scevent.min.js');
    window.snaptr('init', id);
  },
  pageView() { if (window.snaptr) window.snaptr('track', 'PAGE_VIEW'); },
});

// Intercom messenger. Has a clean shutdown, so no reload needed on reject.
const intercom = (appId) => ({
  onAccept() {
    window.intercomSettings = { app_id: appId };
    (function () {
      var w = window;
      var ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
      } else {
        var d = document;
        var i = function () {
          i.c(arguments);
        };
        i.q = [];
        i.c = function (args) {
          i.q.push(args);
        };
        w.Intercom = i;
        var l = function () {
          var s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://widget.intercom.io/widget/' + appId;
          var x = d.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    })();
    window.Intercom('boot', { app_id: appId });
  },
  onReject() {
    if (!window.Intercom) return;
    window.Intercom('shutdown'); // ends the session and clears Intercom's cookies
    // Also drop the global + widget so nothing re-activates it on later navigation
    // (Intercom('update') after a shutdown would boot it again).
    dropGlobals(['Intercom', 'intercomSettings']);
    removeScripts('widget.intercom.io');
    clearCookies(['intercom-id-' + appId, 'intercom-session-' + appId, 'intercom-device-id-' + appId]);
  },
  onRouteChange() {
    if (window.Intercom) window.Intercom('update');
  },
});

// Google Tag Manager container — id is the GTM id (e.g. 'GTM-XXXX'). The consent
// category it's declared under gates the whole container: it loads on accept and
// pushes a route-change event on SPA navigation (parity with
// gatsby-plugin-google-tagmanager). GTM can't be torn down without a reload, so
// onReject leaves it (its Google tags still honor Consent Mode). The optional
// `defaultDataLayer` on the spec is pushed before gtm.js.
const googleTagManager = (id, spec = {}) => ({
  onAccept() {
    injectGtm(id, spec.defaultDataLayer);
  },
  onReject() {},
  onRouteChange() {
    if (window[GTM_FLAG]) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'gatsby-route-change' });
    }
  },
});

/* eslint-enable */

// Merges several trackers into one { onAccept, onReject, onRouteChange } so a
// single consent category can gate multiple trackers (e.g. marketing = FB + Reddit).
const combineTrackers = (trackers) => ({
  onAccept() {
    trackers.forEach((t) => t.onAccept && t.onAccept());
  },
  onReject() {
    trackers.forEach((t) => t.onReject && t.onReject());
  },
  onRouteChange() {
    trackers.forEach((t) => t.onRouteChange && t.onRouteChange());
  },
});

module.exports = {
  facebookPixel,
  redditPixel,
  pinterestPixel,
  linkedInInsight,
  microsoftUet,
  tiktokPixel,
  twitterPixel,
  snapchatPixel,
  intercom,
  googleTagManager,
  combineTrackers,
};
